import { defineModule } from '@nexus/core';
import {
  contactCreated,
  contactDeleted,
  contactUpdated,
  organizationArchived,
  organizationCreated,
  organizationMerged,
  organizationUpdated,
} from './events';

/** Contract between the module and the kernel (docs/architecture/modules.md). */
export const manifest = defineModule({
  id: 'crm',
  name: 'crm.name',
  description: 'crm.description',
  icon: 'building-2',
  base: true,
  permissions: [
    { key: 'crm.organization.read' },
    { key: 'crm.organization.write' },
    { key: 'crm.organization.comment' },
    { key: 'crm.organization.remind' },
    { key: 'crm.organization.export' },
    { key: 'crm.organization.archive', privileged: true },
    { key: 'crm.organization.merge', privileged: true },
    { key: 'crm.contact.read' },
    // Scope "own": only contacts the person created (docs/security/permissions.md).
    { key: 'crm.contact.write', scopes: ['own', 'all'] },
    { key: 'crm.contact.comment' },
    { key: 'crm.contact.remind' },
    { key: 'crm.contact.export' },
    { key: 'crm.contact.delete', privileged: true },
    { key: 'crm.settings.manage', privileged: true },
  ],
  defaultRoles: [
    {
      key: 'reader',
      name: 'crm.roles.reader',
      // Customers are company knowledge: everyone reads them (Peter, 10 October 2026).
      everyone: true,
      grants: [
        { permission: 'crm.organization.read', scope: null },
        { permission: 'crm.contact.read', scope: null },
      ],
    },
    {
      key: 'editor',
      name: 'crm.roles.editor',
      grants: [
        { permission: 'crm.organization.read', scope: null },
        { permission: 'crm.organization.write', scope: null },
        { permission: 'crm.organization.comment', scope: null },
        { permission: 'crm.organization.remind', scope: null },
        { permission: 'crm.organization.export', scope: null },
        { permission: 'crm.contact.read', scope: null },
        { permission: 'crm.contact.write', scope: 'all' },
        { permission: 'crm.contact.comment', scope: null },
        { permission: 'crm.contact.remind', scope: null },
        { permission: 'crm.contact.export', scope: null },
      ],
    },
    {
      key: 'manager',
      name: 'crm.roles.manager',
      grants: [
        { permission: 'crm.organization.*', scope: null },
        // Unscoped permissions of the wildcard ignore the scope.
        { permission: 'crm.contact.*', scope: 'all' },
        { permission: 'crm.settings.manage', scope: null },
      ],
    },
  ],
  navigation: [
    {
      label: 'crm.nav.organizations',
      path: '/crm/organizations',
      icon: 'building-2',
      permission: 'crm.organization.read',
    },
    {
      label: 'crm.nav.contacts',
      path: '/crm/contacts',
      icon: 'users',
      permission: 'crm.contact.read',
    },
  ],
  search: [
    { entity: 'crm.organization', permission: 'crm.organization.read' },
    { entity: 'crm.contact', permission: 'crm.contact.read' },
  ],
  activity: [
    {
      entity: 'crm.organization',
      read: 'crm.organization.read',
      comment: 'crm.organization.comment',
      remind: 'crm.organization.remind',
      path: '/crm/organizations/$id',
    },
    {
      entity: 'crm.contact',
      read: 'crm.contact.read',
      comment: 'crm.contact.comment',
      remind: 'crm.contact.remind',
      path: '/crm/contacts/$id',
    },
  ],
  views: [
    { list: 'crm.organizations', read: 'crm.organization.read', share: 'crm.organization.write' },
    { list: 'crm.contacts', read: 'crm.contact.read', share: 'crm.contact.write' },
  ],
  imports: [
    {
      id: 'crm.organizations',
      label: 'crm.import.organizations',
      permission: 'crm.organization.write',
      fields: [
        {
          key: 'name',
          label: 'crm.fields.name',
          required: true,
          aliases: ['Firma', 'Firmenname', 'Unternehmen', 'Name 1', 'Company', 'Organisation'],
        },
        {
          key: 'shortName',
          label: 'crm.fields.shortName',
          aliases: ['Kurzbezeichnung', 'Matchcode'],
        },
        { key: 'status', label: 'crm.fields.status' },
        { key: 'industry', label: 'crm.fields.industry', aliases: ['Industry'] },
        {
          key: 'vatId',
          label: 'crm.fields.vatId',
          aliases: ['UStIdNr', 'USt-ID', 'VAT', 'VAT ID'],
        },
        { key: 'website', label: 'crm.fields.website', aliases: ['Homepage', 'Internet', 'Web'] },
        { key: 'phone', label: 'crm.fields.phone', aliases: ['Tel', 'Telefon 1', 'Phone'] },
        { key: 'email', label: 'crm.fields.email', aliases: ['Mail', 'E-Mail-Adresse'] },
        {
          key: 'paymentTermDays',
          label: 'crm.fields.paymentTermDays',
          aliases: ['Zahlungsziel', 'Zahlungsziel Tage'],
        },
        {
          key: 'datevDebtor',
          label: 'crm.fields.datevDebtor',
          aliases: ['Debitor', 'Debitorennummer', 'Konto', 'Kontonummer'],
        },
        {
          key: 'street',
          label: 'crm.addresses.fields.street',
          aliases: ['Straße', 'Strasse', 'Street'],
        },
        {
          key: 'houseNumber',
          label: 'crm.addresses.fields.houseNumber',
          aliases: ['Hausnr', 'Nr'],
        },
        {
          key: 'postcode',
          label: 'crm.addresses.fields.postcode',
          aliases: ['PLZ', 'Postleitzahl', 'ZIP'],
        },
        { key: 'city', label: 'crm.addresses.fields.city', aliases: ['Ort', 'Stadt', 'City'] },
        {
          key: 'countryCode',
          label: 'crm.addresses.fields.countryCode',
          aliases: ['Land', 'Country'],
        },
      ],
    },
  ],
  sequences: [
    {
      key: 'crm.organization',
      label: 'crm.sequences.organization',
      format: 'K-{n}',
      start: 10001,
    },
  ],
  // Read-only on devices, so reports of other modules name client, owner and operator without a
  // connection (OrganizationOffline): only those that records on the device name, e.g. the
  // customer of a prepared project. Contact persons stay online.
  offline: [
    {
      entity: 'crm.organization',
      label: 'crm.offline.organizations',
      mode: 'read',
      scope: 'prepared',
      conflict: 'last-write-wins',
      maxAge: 'P30D',
      permissions: { read: 'crm.organization.read' },
      pins: [{ entity: 'crm.organization', field: 'id' }],
    },
  ],
  events: {
    publishes: [
      organizationCreated.type,
      organizationUpdated.type,
      organizationArchived.type,
      organizationMerged.type,
      contactCreated.type,
      contactUpdated.type,
      contactDeleted.type,
    ],
  },
  // Detail pages where dependent modules show sections, e.g. the projects of a customer.
  sectionTargets: ['crm.organization', 'crm.contact'],
  dataProcessing: [
    {
      // Former distribution lists, no longer in the app (they moved to projects). The rows stay
      // until the company decides to drop them; retention and erasure still run (ListData).
      entity: 'crm.list',
      purpose:
        'Verteiler für Rundmails, Einladungen und Grußkarten an Ansprechpartner; wer den Verteiler angelegt hat',
      legalBasis: 'Art. 6 Abs. 1 lit. f DSGVO',
      // Members are references to contacts; their name and address stay with the contact.
      categories: ['account-id', 'contact-reference', 'free-text'],
      // Proposal: a list nobody changed for two years is no longer used.
      retention: { period: 'P2Y', after: 'last-change' },
      erase: 'delete',
    },
    {
      entity: 'crm.organization',
      purpose:
        'Stammdaten der Geschäftspartner einer Firma für Angebote, Projekte und Rechnungen; Betreuer innerhalb der Firma',
      legalBasis: 'Art. 6 Abs. 1 lit. b, c und f DSGVO',
      // A sole trader's name, phone, email and address are personal data; the account manager is an
      // account ID of the company.
      // A sole trader's address can be the home address, with coordinates for the map.
      categories: [
        'company-name',
        'business-contact',
        'postal-address',
        'geo-location',
        'vat-id',
        'account-id',
        'free-text',
      ],
      // Commercial and tax records: ten years after the business relationship ended (§ 147 AO).
      retention: { period: 'P10Y', after: 'inactive' },
      erase: 'anonymize',
    },
    {
      entity: 'crm.contact',
      purpose:
        'Kommunikation mit Ansprechpartnern der Geschäftspartner; Werbewiderspruch; Konto, das den Kontakt angelegt hat',
      legalBasis: 'Art. 6 Abs. 1 lit. b und f DSGVO',
      categories: [
        'name',
        'business-contact',
        'job-title',
        'language',
        'objection',
        'account-id',
        'free-text',
      ],
      // Business letters and mails: ten years after the person left or the organisation became
      // inactive (§ 147 AO, § 257 HGB).
      retention: { period: 'P10Y', after: 'left-or-inactive' },
      erase: 'anonymize',
    },
  ],
});
